import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const authSchema = z.object({
  email: z.string().email("Ongeldig e-mailadres"),
  password: z.string().min(6, "Wachtwoord moet minimaal 6 tekens zijn"),
  rememberMe: z.boolean().optional(),
});

const signupSchema = z
  .object({
    email: z.string().email("Ongeldig e-mailadres"),
    password: z.string().min(6, "Wachtwoord moet minimaal 6 tekens zijn"),
    confirmPassword: z.string().min(6, "Wachtwoord moet minimaal 6 tekens zijn"),
    acceptTerms: z.boolean().refine((val) => val === true, {
      message: "U moet akkoord gaan met de algemene voorwaarden",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Wachtwoorden komen niet overeen",
    path: ["confirmPassword"],
  });

const resetSchema = z.object({
  email: z.string().email("Ongeldig e-mailadres"),
});

const newPasswordSchema = z
  .object({
    password: z.string().min(6, "Wachtwoord moet minimaal 6 tekens zijn"),
    confirmPassword: z.string().min(6, "Wachtwoord moet minimaal 6 tekens zijn"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Wachtwoorden komen niet overeen",
    path: ["confirmPassword"],
  });

type AuthFormData = z.infer<typeof authSchema>;
type SignupFormData = z.infer<typeof signupSchema>;
type ResetFormData = z.infer<typeof resetSchema>;
type NewPasswordFormData = z.infer<typeof newPasswordSchema>;

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signIn, signUp, resetPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "login");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);

  const mode = searchParams.get("mode");

  useEffect(() => {
    if (user && mode !== "reset") {
      navigate("/");
    }

    // Check if this is a password reset link
    if (mode === "reset") {
      setShowNewPasswordForm(true);
    }
  }, [user, navigate, mode]);

  const form = useForm<AuthFormData>({
    resolver: zodResolver(authSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      acceptTerms: false,
    },
  });

  const resetForm = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: "",
    },
  });

  const newPasswordForm = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onLoginSubmit = async (data: AuthFormData) => {
    setLoading(true);

    try {
      const { error } = await signIn(data.email, data.password, data.rememberMe);
      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Onjuiste inloggegevens");
        } else {
          toast.error(error.message);
        }
      } else {
        sessionStorage.setItem("showWelcome", "true");
        toast.success("Succesvol ingelogd");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  const onSignupSubmit = async (data: SignupFormData) => {
    setLoading(true);

    try {
      const { error } = await signUp(data.email, data.password);
      if (error) {
        if (error.message.includes("User already registered")) {
          toast.error("Dit e-mailadres is al geregistreerd");
        } else {
          toast.error(error.message);
        }
      } else {
        setVerificationDialogOpen(true);
        signupForm.reset();
      }
    } finally {
      setLoading(false);
    }
  };

  const onResetSubmit = async (data: ResetFormData) => {
    setLoading(true);
    try {
      const { error } = await resetPassword(data.email);
      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Er is een e-mail verzonden met instructies om uw wachtwoord te resetten.");
        setResetDialogOpen(false);
        resetForm.reset();
      }
    } finally {
      setLoading(false);
    }
  };

  const onNewPasswordSubmit = async (data: NewPasswordFormData) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Uw wachtwoord is succesvol gewijzigd!");
        setShowNewPasswordForm(false);
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  if (showNewPasswordForm) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <Card className="w-full max-w-md p-8">
          <h1 className="text-2xl font-bold text-center mb-2">Nieuw wachtwoord instellen</h1>
          <p className="text-center text-muted-foreground mb-6">Voer uw nieuwe wachtwoord in</p>

          <Form {...newPasswordForm}>
            <form onSubmit={newPasswordForm.handleSubmit(onNewPasswordSubmit)} className="space-y-4">
              <FormField
                control={newPasswordForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nieuw wachtwoord</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" placeholder="••••••••" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={newPasswordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bevestig wachtwoord</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" placeholder="••••••••" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Bezig..." : "Wachtwoord Wijzigen"}
              </Button>
            </form>
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <Dialog
        open={verificationDialogOpen}
        onOpenChange={(open) => {
          setVerificationDialogOpen(open);
          if (!open) {
            setActiveTab("login");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verifieer uw e-mailadres</DialogTitle>
            <DialogDescription>
              We hebben een verificatie-e-mail verzonden naar uw e-mailadres. Klik op de link in de e-mail om uw account
              te activeren. Na verificatie kunt u inloggen en toegang krijgen tot de API-catalogus.
            </DialogDescription>
          </DialogHeader>
          <Button
            onClick={() => {
              setVerificationDialogOpen(false);
            }}
          >
            Begrepen
          </Button>
        </DialogContent>
      </Dialog>

      <Card className="w-full max-w-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Start met een API-aanvraag</h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="login">Inloggen</TabsTrigger>
            <TabsTrigger value="signup">Registreren</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onLoginSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mailadres</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" placeholder="uw.email@example.com" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Wachtwoord</FormLabel>
                      <FormControl>
                        <Input {...field} type="password" placeholder="••••••••" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rememberMe"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="text-sm font-normal cursor-pointer">Mijn gegevens onthouden</FormLabel>
                    </FormItem>
                  )}
                />

                <div className="flex justify-end">
                  <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="link" type="button" className="px-0 text-sm">
                        Wachtwoord vergeten?
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Wachtwoord resetten</DialogTitle>
                        <DialogDescription>
                          Voer uw e-mailadres in en we sturen u instructies om uw wachtwoord te resetten.
                        </DialogDescription>
                      </DialogHeader>
                      <Form {...resetForm}>
                        <form onSubmit={resetForm.handleSubmit(onResetSubmit)} className="space-y-4">
                          <FormField
                            control={resetForm.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>E-mailadres</FormLabel>
                                <FormControl>
                                  <Input {...field} type="email" placeholder="uw.email@example.com" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <div className="flex gap-2 justify-end">
                            <Button type="button" variant="outline" onClick={() => setResetDialogOpen(false)}>
                              Annuleren
                            </Button>
                            <Button type="submit" disabled={loading}>
                              {loading ? "Bezig..." : "Versturen"}
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </DialogContent>
                  </Dialog>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Bezig met inloggen..." : "Inloggen"}
                </Button>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="signup">
            <Form {...signupForm}>
              <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="space-y-4">
                <FormField
                  control={signupForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mailadres</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" placeholder="uw.email@example.com" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={signupForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Wachtwoord</FormLabel>
                      <FormControl>
                        <Input {...field} type="password" placeholder="••••••••" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={signupForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bevestig wachtwoord</FormLabel>
                      <FormControl>
                        <Input {...field} type="password" placeholder="••••••••" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={signupForm.control}
                  name="acceptTerms"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-normal cursor-pointer">
                          Ik ga akkoord met de algemene voorwaarden
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Bezig met registreren..." : "Account aanmaken"}
                </Button>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
